import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Check, Crown, Upload, Trash2, Building2 } from "lucide-react";

const MAX_LOGO_BYTES = 2 * 1024 * 1024;

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const { t } = useI18n();
  const canManage = user && (user.role === "owner" || user.role === "admin");
  const fileRef = useRef(null);

  const [plans, setPlans] = useState([]);
  const [company, setCompany] = useState({
    name: "", legal_name: "", logo: "", gst_number: "", qst_number: "", address: "", phone: "", website: "",
  });
  const [savingCompany, setSavingCompany] = useState(false);
  const [savingPlan, setSavingPlan] = useState(null);
  const dirtyRef = useRef(false);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {});
    api.get("/company")
      .then(({ data }) => { if (!dirtyRef.current) setCompany((c) => ({ ...c, ...data })); })
      .catch(() => {});
  }, []);

  const set = (k) => (e) => { dirtyRef.current = true; setCompany((c) => ({ ...c, [k]: e.target.value })); };

  const onLogoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      toast.error(t("dash.pages.settings.logoTooBig"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { dirtyRef.current = true; setCompany((c) => ({ ...c, logo: reader.result })); };
    reader.readAsDataURL(file);
  };

  const saveCompany = async () => {
    setSavingCompany(true);
    try {
      await api.patch("/company", {
        company_name: company.name,
        legal_name: company.legal_name,
        logo: company.logo,
        gst_number: company.gst_number,
        qst_number: company.qst_number,
        address: company.address,
        phone: company.phone,
        website: company.website,
      });
      await refreshUser();
      toast.success(t("dash.pages.settings.savedName"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingCompany(false);
    }
  };

  const changePlan = async (planKey) => {
    if (planKey === user.plan) return;
    setSavingPlan(planKey);
    try {
      await api.patch("/company", { plan: planKey });
      await refreshUser();
      toast.success(t("dash.pages.settings.changed"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingPlan(null);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-extrabold text-white">{t("dash.pages.settings.title")}</h1>
        <p className="mt-1 text-white/55">{t("dash.pages.settings.desc")}</p>
      </div>

      {/* Company profile */}
      <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-6" data-testid="company-profile-card">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#EAB308]" /> {t("dash.pages.settings.company")}
        </h2>

        {/* Logo */}
        <div className="mt-5 flex items-center gap-5">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-black/40 overflow-hidden">
            {company.logo ? (
              <img src={company.logo} alt="logo" className="h-full w-full object-contain" data-testid="company-logo-preview" />
            ) : (
              <Building2 className="w-8 h-8 text-white/25" />
            )}
          </div>
          <div>
            <Label className="text-white/80">{t("dash.pages.settings.logo")}</Label>
            <p className="text-xs text-white/40 mt-0.5">{t("dash.pages.settings.logoHint")}</p>
            <div className="mt-2 flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onLogoPick} data-testid="logo-input" />
              <Button
                data-testid="upload-logo-btn"
                disabled={!canManage}
                onClick={() => fileRef.current?.click()}
                className="h-9 bg-white/10 border border-white/15 text-white text-xs gap-1.5 hover:bg-white/15"
              >
                <Upload className="w-3.5 h-3.5" /> {t("dash.pages.settings.uploadLogo")}
              </Button>
              {company.logo && canManage && (
                <Button
                  data-testid="remove-logo-btn"
                  onClick={() => { dirtyRef.current = true; setCompany((c) => ({ ...c, logo: "" })); }}
                  className="h-9 bg-transparent border border-white/15 text-white/60 text-xs gap-1.5 hover:text-[#dc143c] hover:border-[#dc143c]/40"
                >
                  <Trash2 className="w-3.5 h-3.5" /> {t("dash.pages.settings.removeLogo")}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Names */}
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-white/80">{t("dash.pages.settings.companyName")}</Label>
            <Input data-testid="settings-company-name" disabled={!canManage} value={company.name || ""} onChange={set("name")} className="bg-black/40 border-white/15 text-white h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-white/80">{t("dash.pages.settings.legalName")}</Label>
            <Input data-testid="settings-legal-name" disabled={!canManage} value={company.legal_name || ""} onChange={set("legal_name")} className="bg-black/40 border-white/15 text-white h-11" />
          </div>
        </div>

        {/* Taxes */}
        <div className="mt-6">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40 mb-2">{t("dash.pages.settings.taxes")}</div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-white/80">{t("dash.pages.settings.gst")}</Label>
              <Input data-testid="settings-gst" disabled={!canManage} placeholder="123456789 RT0001" value={company.gst_number || ""} onChange={set("gst_number")} className="bg-black/40 border-white/15 text-white h-11 font-mono" />
            </div>
            <div className="space-y-2">
              <Label className="text-white/80">{t("dash.pages.settings.qst")}</Label>
              <Input data-testid="settings-qst" disabled={!canManage} placeholder="1234567890 TQ0001" value={company.qst_number || ""} onChange={set("qst_number")} className="bg-black/40 border-white/15 text-white h-11 font-mono" />
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="mt-6">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40 mb-2">{t("dash.pages.settings.contact")}</div>
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label className="text-white/80">{t("dash.pages.settings.address")}</Label>
              <Input data-testid="settings-address" disabled={!canManage} value={company.address || ""} onChange={set("address")} className="bg-black/40 border-white/15 text-white h-11" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-white/80">{t("dash.pages.settings.phone")}</Label>
                <Input data-testid="settings-phone" disabled={!canManage} value={company.phone || ""} onChange={set("phone")} className="bg-black/40 border-white/15 text-white h-11" />
              </div>
              <div className="space-y-2">
                <Label className="text-white/80">{t("dash.pages.settings.website")}</Label>
                <Input data-testid="settings-website" disabled={!canManage} placeholder="https://" value={company.website || ""} onChange={set("website")} className="bg-black/40 border-white/15 text-white h-11" />
              </div>
            </div>
          </div>
        </div>

        {canManage && (
          <div className="mt-6 flex justify-end">
            <Button
              data-testid="settings-save-company"
              onClick={saveCompany} disabled={savingCompany}
              className="h-11 px-6 bg-[#EAB308] text-black font-semibold rounded-md hover:opacity-90"
            >
              {savingCompany ? t("dash.pages.settings.saving") : t("dash.pages.settings.save")}
            </Button>
          </div>
        )}
      </div>

      {/* Plan */}
      <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-6">
        <h2 className="text-lg font-bold text-white">{t("dash.pages.settings.planTitle")}</h2>
        <p className="mt-1 text-sm text-white/50">{t("dash.pages.settings.planDesc")}</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {plans.map((p) => {
            const active = p.key === user.plan;
            const isEco = p.key === "ecosysteme";
            return (
              <div
                key={p.key}
                data-testid={`settings-plan-${p.key}`}
                className={`rounded-lg border p-5 transition-colors ${
                  active ? "border-[#EAB308]/60 bg-[#EAB308]/[0.06]" : "border-white/10 bg-black/30"
                }`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-white flex items-center gap-2">
                    {isEco && <Crown className="w-4 h-4 text-[#EAB308]" />}
                    {t(`dash.plan.${p.key}`)}
                  </h3>
                  {active && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#EAB308]/15 border border-[#EAB308]/30 px-2.5 py-0.5 text-[11px] font-medium text-[#EAB308]">
                      <Check className="w-3 h-3" /> {t("dash.pages.settings.active")}
                    </span>
                  )}
                </div>
                <div className="mt-2 text-2xl font-extrabold text-white font-mono">
                  {p.price === "Sur mesure" ? t("dash.pages.settings.custom") : `${p.price}$`}
                  <span className="text-sm text-white/40 font-sans">{p.period ? t("dash.pages.settings.perMonth") : ""}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {p.modules.map((mk) => (
                    <span key={mk} className="rounded bg-white/5 px-2 py-0.5 text-[11px] text-white/50 uppercase font-mono">{mk}</span>
                  ))}
                </div>
                <Button
                  data-testid={`settings-select-plan-${p.key}`}
                  onClick={() => changePlan(p.key)}
                  disabled={active || savingPlan === p.key || !canManage}
                  className={`mt-4 w-full rounded-md font-semibold transition-opacity ${
                    active
                      ? "bg-white/5 text-white/40 cursor-default"
                      : "bg-[#EAB308] text-black hover:opacity-90"
                  }`}
                >
                  {active ? t("dash.pages.settings.current") : savingPlan === p.key ? t("dash.pages.settings.saving") : t("dash.pages.settings.choose")}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="mt-4 text-xs text-white/30">
          {t("dash.pages.settings.stripeNote")}
        </p>
      </div>
    </div>
  );
}
